/**
 * Merging duplicate partner records into one.
 *
 * A full page rather than a dialog, because a merge is the one operation here
 * that retires records: it needs room to say what it will do, and it should not
 * feel like something you can dismiss with Escape halfway through.
 *
 * Three steps, and the middle one is the point. Choosing records is easy;
 * understanding that 7,489 registrations are about to change hands, that two
 * locations move with them, and that one record cannot be merged at all
 * because an API key files against it - that is the decision. The wizard
 * will not let you past it without showing all three.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import {
  AlertTriangle,
  Check,
  ChevronRight,
  Loader2,
  Search,
} from 'lucide-react'
import { toast } from 'sonner'

import { AppShell } from '@/admin/components/app-shell'
import { DataTable } from '@/admin/components/data-table'
import { usePartnershipDetail, usePartnerships } from '@/admin/hooks/use-partnerships'
import { PartnerRowLabel } from '@/admin/pages/partnerships/partner-row-label'
import {
  TERMINAL_MERGE_STATUSES,
  useCreateMergePlan,
  useDismissDuplicateGroup,
  useMergeImpact,
  usePartnerDuplicates,
  type DuplicateGroupMember,
  useMergePlan,
  useStartMergePlan,
  type MergePlan,
} from '@/admin/hooks/use-partner-merge'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { toastApiError } from '@/shared/lib/api-error'
import { useDebouncedValue } from '@/shared/hooks/use-debounced-value'

type Step = 'choose' | 'review' | 'running'

/** Drives the "step N of M" indicator; order is the order they happen in. */
const STEP_ORDER: Step[] = ['choose', 'review', 'running']

// Shown when the API has no translation for a flag yet. The wording says what
// the operator should DO about it, not which field disagreed.
const VERDICT_FLAG_FALLBACK: Record<string, string> = {
  parent_conflict:
    'These records sit under different parent groups, so merging them would move registrations between dealer groups.',
  parent_disagrees:
    'These records share an account number but sit under different parents.',
  parent_partially_set: 'Some of these records have a parent group and some do not.',
  multiple_account_codes:
    'These records have different account numbers, which usually means they are different branches.',
  multiple_email_domains: 'These records use different email domains.',
  no_registrations_anywhere: 'None of these records holds any registrations.',
  registration_count_tie:
    'Two records hold the same number of registrations, so the one to keep is not obvious.',
}

export function MergePartnersPage() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { uuid: survivorUuid } = useParams<{ uuid: string }>()
  const [searchParams] = useSearchParams()

  const [step, setStep] = useState<Step>('choose')
  const [search, setSearch] = useState('')
  const [selected, setSelected] = useState<Record<string, string>>({})
  // {code_name: chosen value} where the records disagree. Empty means "keep the
  // survivor's", which is what the merge did silently before this existed.
  const [fieldChoices, setFieldChoices] = useState<Record<string, string>>({})
  const [plan, setPlan] = useState<MergePlan | null>(null)
  // A group the scan bucketed 'blocked' can still be merged, but only after
  // the operator has seen WHY and said so. The button stays enabled; the click
  // surfaces the reason.
  const [verdictAcknowledged, setVerdictAcknowledged] = useState(false)
  const [verdictError, setVerdictError] = useState(false)
  const verdictRef = useRef<HTMLDivElement>(null)
  const debouncedSearch = useDebouncedValue(search, 250)

  const { data: survivor } = usePartnershipDetail(survivorUuid ?? null)
  const { data: results, isFetching: searching } = usePartnerships(
    debouncedSearch.length >= 2
      ? { search: debouncedSearch, page_size: '15' }
      : undefined,
  )

  // Arriving from "Review duplicates" on the partner page: the scan already
  // worked out which records look like this one, so start with them ticked
  // rather than making the operator retype the partner's own name and rebuild
  // a selection the queue already knows. Names come from the group, so a uuid
  // we cannot label is left out rather than shown raw.
  const { data: duplicateGroup } = usePartnerDuplicates(survivorUuid)
  const dismissGroup = useDismissDuplicateGroup()
  const preselectApplied = useRef(false)
  useEffect(() => {
    if (preselectApplied.current) return
    const wanted = (searchParams.get('preselect') ?? '')
      .split(',')
      .map((u) => u.trim())
      .filter(Boolean)
    if (!wanted.length || !duplicateGroup?.members?.length) return
    const byUuid = new Map(
      duplicateGroup.members
        .filter((m) => m.partnership_uuid && m.name)
        .map((m) => [m.partnership_uuid, m.name as string]),
    )
    const next: Record<string, string> = {}
    for (const uuid of wanted) {
      const name = byUuid.get(uuid)
      if (name && uuid !== survivorUuid) next[uuid] = name
    }
    if (Object.keys(next).length) {
      setSelected(next)
      preselectApplied.current = true
    }
  }, [searchParams, duplicateGroup, survivorUuid])

  const candidates = useMemo(
    () => (results?.results ?? []).filter((p) => p.uuid !== survivorUuid),
    [results, survivorUuid],
  )
  const selectedUuids = useMemo(() => Object.keys(selected), [selected])

  const hasAccountNumbers = (duplicateGroup?.members ?? []).some((member) =>
    (member.account_number ?? '').trim(),
  )
  const verdict = duplicateGroup?.bucket ?? ''
  const isBlocked = verdict === 'blocked'
  // Plain English, because 'parent_conflict' is not a sentence.
  const verdictReasons = (duplicateGroup?.flags ?? []).map((flag) =>
    t(`partnerships.mergeFlag.${flag}`, VERDICT_FLAG_FALLBACK[flag] ?? flag),
  )
  const locationsByUuid = useMemo(() => {
    const map: Record<string, string> = {}
    for (const member of duplicateGroup?.members ?? []) {
      const places = (member.top_locations ?? [])
        .map((l) => `${l.place} (${l.count.toLocaleString()})`)
        .join(', ')
      if (places) map[member.partnership_uuid] = places
    }
    return map
  }, [duplicateGroup])
  // Every record in this group shares a name, so a joined list of names says
  // nothing. What separates them is the account number and where their
  // customers are.
  //
  // Ordered BY ACCOUNT NUMBER, not by size: the question being asked is "how
  // many businesses am I looking at", and that is only legible when the
  // repeats sit together. It also puts near-miss codes side by side, so a code
  // that differs from its neighbour by one character reads as the typo it
  // probably is. Records with no account number sort last, and within one code
  // the biggest book leads, since that is the likely record to keep.
  const hasLocations = Object.keys(locationsByUuid).length > 0
  // A column with nothing in it is not evidence. Plenty of tenants carry no
  // account number at all (one in this book has 541 dealer records and not a
  // single one), and a group with no registrations has no customers to place,
  // so the table is built from whichever signals this group actually has and
  // stands down when it has none.
  const groupEvidence = useMemo(
    () =>
      [...(duplicateGroup?.members ?? [])].sort((a, b) => {
        const left = (a.account_number ?? '').trim()
        const right = (b.account_number ?? '').trim()
        if (left !== right) {
          if (!left) return 1
          if (!right) return -1
          return left.localeCompare(right)
        }
        return (b.registration_count ?? 0) - (a.registration_count ?? 0)
      }),
    [duplicateGroup],
  )

  const { data: impact, isPending: impactPending } = useMergeImpact(
    survivorUuid,
    selectedUuids,
  )
  const createPlan = useCreateMergePlan(survivorUuid)
  const startPlan = useStartMergePlan()
  // Once a plan exists the poll is the source of truth, not the create response.
  const { data: livePlan } = useMergePlan(
    step === 'running' ? plan?.uuid : undefined,
  )
  const current = livePlan ?? plan

  // i18next pluralises on `count`, and a key can only have one. So each number
  // is rendered by its own pluralised key and the sentence embeds the results -
  // otherwise a single-record merge reads "1 people".
  const countRegistrations = (n: number) =>
    t('partnerships.mergeCountRegistrations', {
      count: n,
      defaultValue_one: '{{count}} registration',
      defaultValue_other: '{{count}} registrations',
    })
  const countPeople = (n: number) =>
    t('partnerships.mergeCountPeople', {
      count: n,
      defaultValue_one: '{{count}} person',
      defaultValue_other: '{{count}} people',
    })

  const toggle = (uuid: string, name: string) =>
    setSelected((prev) => {
      const next = { ...prev }
      if (next[uuid]) delete next[uuid]
      else next[uuid] = name
      return next
    })

  const goToReview = async () => {
    if (selectedUuids.length === 0) {
      toast.error(
        t('partnerships.mergePickRecords', 'Choose at least one record to merge in.'),
      )
      return
    }
    // The scan says these are not one business. Merging anyway moves
    // registrations - and the customers on them - between real dealerships, so
    // it takes a deliberate acknowledgement rather than a default-yes.
    if (isBlocked && !verdictAcknowledged) {
      setVerdictError(true)
      verdictRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      return
    }
    try {
      const created = await createPlan.mutateAsync({
        loserUuids: selectedUuids,
        fieldChoices,
      })
      setPlan(created)
      setStep('review')
    } catch (err) {
      toastApiError(err, t('partnerships.mergePlanFailed', 'Could not plan this merge.'))
    }
  }

  const confirm = async () => {
    if (!plan) return
    try {
      await startPlan.mutateAsync(plan.uuid)
      setStep('running')
    } catch (err) {
      toastApiError(err, t('partnerships.mergeStartFailed', 'Could not start this merge.'))
    }
  }

  const survivorName = survivor?.downstream_organisation?.name ?? ''
  const isDone =
    !!current?.status && TERMINAL_MERGE_STATUSES.includes(current.status)

  return (
    <AppShell>
      <main className="page-container py-10" data-testid="merge-page">
        {/* A focused task, not a page: where you are, how far through, and one
            explicit way out. A merge retires records, so the operator should
            never be unsure which step they are on or how to leave. */}
        <div className="mb-8 flex flex-wrap items-center justify-between gap-2 border-b-[0.5px] border-b-[var(--rule)] pb-3">
          <nav
            aria-label={t('partnerships.mergeBreadcrumb', 'Merge duplicates')}
            className="flex items-center gap-2 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
          >
            <Button
              variant="ghost"
              size="xs"
              onClick={() => navigate('/network/partners')}
              className="h-auto border-none bg-transparent p-0 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              {t('partnerships.mergeCrumbNetwork', 'Network')}
            </Button>
            <ChevronRight className="h-3 w-3" />
            <Button
              variant="ghost"
              size="xs"
              onClick={() => navigate(`/network/${survivorUuid}`)}
              className="h-auto max-w-[220px] truncate border-none bg-transparent p-0 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              {survivorName}
            </Button>
            <ChevronRight className="h-3 w-3" />
            <span className="text-[var(--ink-2)]">
              {t('partnerships.mergeCrumbTask', 'Merge duplicates')}
            </span>
          </nav>
          <div className="flex items-center gap-3 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
            <span data-testid="merge-step-indicator">
              {t('partnerships.mergeStepOf', 'Step {{current}} of {{total}}', {
                current: STEP_ORDER.indexOf(step) + 1,
                total: STEP_ORDER.length,
              })}
            </span>
            <span aria-hidden>·</span>
            <Button
              variant="ghost"
              size="xs"
              data-testid="merge-exit"
              onClick={() => navigate(`/network/${survivorUuid}`)}
              className="h-auto border-none bg-transparent p-0 text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]"
            >
              {t('partnerships.mergeExit', 'Exit')}
            </Button>
          </div>
        </div>

        <h1 className="clara-title mb-1">
          {t('partnerships.mergeTitle', 'Merge records into {{survivor}}', {
            survivor: survivorName,
          })}
        </h1>
        <p className="mb-8 text-[13px] text-[var(--ink-3)]">
          {t(
            'partnerships.mergeIntro',
            'The records you choose are retired. Their registrations, people and history move to the record you are keeping.',
          )}
        </p>

        {step === 'choose' && (
          <section className="space-y-4" data-testid="merge-step-choose">
            {/* The scan's verdict, where the operator is about to act on it.
                Without this the queue says "blocked" and this page says
                nothing, which is how six real branches nearly became one. */}
            {verdictReasons.length > 0 && (
              <div
                ref={verdictRef}
                data-testid="merge-verdict"
                className={`rounded-lg border-[0.5px] px-4 py-3 ${
                  isBlocked
                    ? 'border-[var(--status-error)] bg-[var(--status-error-bg,var(--bg-light))]'
                    : 'border-[var(--rule-strong)] bg-[var(--bg-light)]'
                }`}
              >
                <div className="flex items-start gap-2">
                  <AlertTriangle
                    className={`mt-0.5 h-4 w-4 shrink-0 ${
                      isBlocked ? 'text-[var(--status-error)]' : 'text-[var(--ink-3)]'
                    }`}
                  />
                  <div className="min-w-0 space-y-1">
                    <p className="text-[13px] font-medium">
                      {isBlocked
                        ? t(
                            'partnerships.mergeVerdictBlocked',
                            'These do not look like one business',
                          )
                        : t(
                            'partnerships.mergeVerdictReview',
                            'Worth a look before you merge',
                          )}
                    </p>
                    <ul className="space-y-0.5 text-[12px] text-[var(--ink-3)]">
                      {verdictReasons.map((reason) => (
                        <li key={reason}>{reason}</li>
                      ))}
                    </ul>
                    {(duplicateGroup?.account_numbers ?? []).length > 1 && (
                      <p className="text-[12px] text-[var(--ink-3)]">
                        {t(
                          'partnerships.mergeVerdictAccounts',
                          'Account numbers here: {{accounts}}',
                          {
                            accounts: (
                              duplicateGroup?.account_numbers ?? []
                            ).join(', '),
                          },
                        )}
                      </p>
                    )}
                    {isBlocked && (
                      <label
                        htmlFor="merge-verdict-ack"
                        className="mt-2 flex cursor-pointer items-start gap-2 text-[12px]"
                      >
                        <Checkbox
                          id="merge-verdict-ack"
                          data-testid="merge-verdict-ack"
                          checked={verdictAcknowledged}
                          onCheckedChange={(next) => {
                            setVerdictAcknowledged(next === true)
                            if (next === true) setVerdictError(false)
                          }}
                        />
                        <span
                          className={
                            verdictError
                              ? 'text-[var(--status-error)]'
                              : 'text-[var(--ink-2)]'
                          }
                        >
                          {t(
                            'partnerships.mergeVerdictAcknowledge',
                            'I have checked these and they are the same business.',
                          )}
                        </span>
                      </label>
                    )}
                    {verdictError && (
                      <p className="text-[12px] text-[var(--status-error)]">
                        {t(
                          'partnerships.mergeVerdictNeedsAck',
                          'Confirm you have checked these before carrying on.',
                        )}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {groupEvidence.length > 1 && (hasAccountNumbers || hasLocations) && (
              <div data-testid="merge-evidence" className="space-y-2">
                <p className="text-[11px] uppercase tracking-[0.12em] text-[var(--ink-3)]">
                  {t(
                    'partnerships.mergeEvidenceHeading',
                    'What is different about these records',
                  )}
                </p>
                <DataTable<DuplicateGroupMember>
                  tableId="merge-duplicate-evidence"
                  rowKey="partnership_uuid"
                  data={groupEvidence}
                  columns={[
                    ...(hasAccountNumbers
                      ? [
                          {
                            id: 'account_number',
                            header: t(
                              'partnerships.mergeEvidenceAccount',
                              'Account number',
                            ),
                            cell: (row: DuplicateGroupMember) => (
                              <span className="font-mono text-[11px]">
                                {row.account_number ||
                                  t(
                                    'partnerships.mergeEvidenceNoAccount',
                                    'not set',
                                  )}
                              </span>
                            ),
                          },
                        ]
                      : []),
                    {
                      id: 'registration_count',
                      header: t(
                        'partnerships.mergeEvidenceRegistrations',
                        'Registrations',
                      ),
                      cell: (row: DuplicateGroupMember) =>
                        (row.registration_count ?? 0).toLocaleString(),
                    },
                    ...(hasLocations
                      ? [
                          {
                            id: 'locations',
                            header: t(
                              'partnerships.mergeEvidenceLocations',
                              'Where their customers are',
                            ),
                            cell: (row: DuplicateGroupMember) => (
                              <span className="text-[var(--ink-3)]">
                                {locationsByUuid[row.partnership_uuid] ||
                                  t(
                                    'partnerships.mergeEvidenceNoLocations',
                                    'no address data',
                                  )}
                              </span>
                            ),
                          },
                        ]
                      : []),
                  ]}
                />
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="merge-search">
                {t('partnerships.mergeFindLabel', 'Records to merge in')}
              </Label>
              <div className="relative max-w-[420px]">
                <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--ink-3)]" />
                <Input
                  id="merge-search"
                  data-testid="merge-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder={t(
                    'partnerships.mergeSearchPlaceholder',
                    'Search partners by name',
                  )}
                  className="pl-8"
                />
              </div>
            </div>

            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)]">
              {/* Loading gated before empty, so a cached-empty result cannot
                  flash "no partners" over an in-flight search. */}
              {debouncedSearch.length < 2 ? (
                <p className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                  {t(
                    'partnerships.mergeSearchHint',
                    'Type at least two characters to find records.',
                  )}
                </p>
              ) : searching && candidates.length === 0 ? (
                <p className="flex items-center gap-2 px-4 py-4 text-[12px] text-[var(--ink-3)]">
                  <Loader2 className="h-3 w-3 animate-spin" />
                  {t('partnerships.mergeSearching', 'Searching')}
                </p>
              ) : candidates.length === 0 ? (
                <p className="px-4 py-4 text-[12px] text-[var(--ink-3)]">
                  {t('partnerships.mergeNoMatches', 'No partners match that.')}
                </p>
              ) : (
                candidates.map((p) => {
                  const name = p.downstream_organisation?.name ?? ''
                  return (
                    <label
                      key={p.uuid}
                      htmlFor={`merge-pick-${p.uuid}`}
                      className="flex cursor-pointer items-center gap-3 border-b-[0.5px] border-b-[var(--rule)] px-4 py-3 text-[13px] last:border-b-0 hover:bg-[var(--bg-light)]"
                    >
                      <Checkbox
                        id={`merge-pick-${p.uuid}`}
                        data-testid={`merge-pick-${p.uuid}`}
                        checked={!!selected[p.uuid]}
                        onCheckedChange={() => toggle(p.uuid, name)}
                      />
                      <span className="flex min-w-0 flex-1">
                        <PartnerRowLabel
                          name={name}
                          attributeSummary={p.attribute_summary}
                        />
                      </span>
                      {locationsByUuid[p.uuid] && (
                        <span
                          className="hidden max-w-[42%] truncate text-[12px] text-[var(--ink-3)] sm:inline"
                          title={locationsByUuid[p.uuid]}
                        >
                          {locationsByUuid[p.uuid]}
                        </span>
                      )}
                      <span className="text-[12px] text-[var(--ink-3)]">
                        {t(
                          'partnerships.mergeRowRegistrations',
                          '{{registrations}} registrations',
                          {
                            registrations: (
                              p.registration_count ?? 0
                            ).toLocaleString(),
                          },
                        )}
                      </span>
                      {!p.is_active && (
                        <Badge variant="outline">
                          {t('partnerships.regroupSuspendedTag', 'Suspended')}
                        </Badge>
                      )}
                    </label>
                  )
                })
              )}
            </div>

            {selectedUuids.length > 0 && (
              <div
                className="rounded-[8px] border-[0.5px] border-[var(--rule)] bg-[var(--bg-light)] p-4 text-[13px]"
                data-testid="merge-running-impact"
              >
                <p className="mb-1 text-[var(--ink)]">
                  {selectedUuids.length === 1
                    ? t('partnerships.mergeSelectedCountOne', '1 record selected')
                    : t(
                        'partnerships.mergeSelectedCount',
                        '{{count}} records selected',
                        { count: selectedUuids.length },
                      )}
                </p>
                {impactPending ? (
                  <p className="flex items-center gap-2 text-[12px] text-[var(--ink-3)]">
                    <Loader2 className="h-3 w-3 animate-spin" />
                    {t('partnerships.mergeMeasuring', 'Working out what moves')}
                  </p>
                ) : impact ? (
                  <p className="text-[12px] text-[var(--ink-2)]">
                    {!!impact.locations_moved?.length &&
                      t(
                        'partnerships.mergeLocationsLine',
                        '{{count}} locations move too. ',
                        { count: impact.locations_moved.length },
                      )}
                    {t(
                      'partnerships.mergeImpactLine',
                      '{{registrations}} and {{people}} move to {{survivor}}.',
                      {
                        registrations: countRegistrations(
                          impact.registrations_affected,
                        ),
                        people: countPeople(impact.people_affected),
                        survivor: survivorName,
                      },
                    )}
                  </p>
                ) : null}
              </div>
            )}

            {/* Both sides of the trade, side by side. "12 records selected"
                does not tell an operator what they are giving up; the record
                being kept and the records being retired, each with what they
                carry, does. */}
            {selectedUuids.length > 0 && (
              <div
                className="grid gap-4 md:grid-cols-2"
                data-testid="merge-comparison"
              >
                <div className="rounded-[8px] border-[0.5px] border-[var(--green)] bg-[var(--bg-light)] p-5">
                  <p className="clara-label mb-2 text-[var(--green)]">
                    {/* Not "which do you want to keep?" - the operator arrived
                        from this record, or the queue proposed it as the one
                        with the largest book. Asking again would imply a choice
                        this step does not offer. */}
                    {t('partnerships.mergeKeeping', 'Keeping')}
                  </p>
                  <p className="text-[15px] text-[var(--ink)]">{survivorName}</p>
                  <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                    {[
                      countRegistrations(survivor?.registration_count ?? 0),
                      countPeople(survivor?.total_user_access_count ?? 0),
                      survivor?.parent_partnership?.downstream_organisation?.name
                        ? t('partnerships.mergeParentIs', 'parent: {{name}}', {
                            name: survivor.parent_partnership
                              .downstream_organisation.name,
                          })
                        : null,
                    ]
                      .filter(Boolean)
                      .join('  ·  ')}
                  </p>
                </div>
                <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5">
                  <p className="clara-label mb-2">
                    {t('partnerships.mergeMergingIn', {
                      count: selectedUuids.length,
                      defaultValue_one: 'Merging in - 1 record',
                      defaultValue_other: 'Merging in - {{count}} records',
                    })}
                  </p>
                  <p className="truncate text-[15px] text-[var(--ink)]">
                    {Object.values(selected).slice(0, 3).join(', ')}
                    {selectedUuids.length > 3 && ' …'}
                  </p>
                  <p className="mt-1 text-[12px] text-[var(--ink-3)]">
                    {impact
                      ? [
                          countRegistrations(impact.registrations_affected),
                          countPeople(impact.people_affected),
                          impact.locations_moved?.length
                            ? t(
                                'partnerships.mergeLocationsCount',
                                '{{count}} locations',
                                { count: impact.locations_moved.length },
                              )
                            : null,
                        ]
                          .filter(Boolean)
                          .join('  ·  ')
                      : t('partnerships.mergeMeasuring', 'Working out what moves')}
                  </p>
                </div>
              </div>
            )}

            {!!impact?.conflicts?.length && (
              <div
                className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5"
                data-testid="merge-conflicts"
              >
                <p className="clara-label mb-1">
                  {t(
                    'partnerships.mergeConflictsTitle',
                    'They disagree on these - pick one each',
                  )}
                </p>
                <p className="mb-4 text-[12px] text-[var(--ink-3)]">
                  {t(
                    'partnerships.mergeConflictsHint',
                    'Whichever you pick is what the record you keep will carry. The others are not kept.',
                  )}
                </p>
                <div className="space-y-3">
                  {impact.conflicts.map((conflict) => {
                    const chosen =
                      fieldChoices[conflict.code_name] ??
                      conflict.options.find((o) => o.is_survivor)?.value ??
                      conflict.options[0]?.value
                    // A value that matches no chip is one the operator typed.
                    const isCustom = !conflict.options.some(
                      (o) => o.value === chosen,
                    )
                    return (
                      <div
                        key={conflict.code_name}
                        className="flex flex-wrap items-center justify-between gap-2 border-b-[0.5px] border-b-[var(--rule)] pb-3 last:border-b-0 last:pb-0"
                      >
                        <span className="text-[13px] text-[var(--ink)]">
                          {conflict.display_name}
                        </span>
                        <div className="flex flex-wrap items-center gap-1.5">
                          {conflict.options.map((option) => (
                            <Button
                              key={option.value}
                              type="button"
                              variant={
                                option.value === chosen ? 'default' : 'outline'
                              }
                              size="xs"
                              data-testid={`merge-conflict-${conflict.code_name}-${option.value}`}
                              onClick={() =>
                                setFieldChoices((prev) => ({
                                  ...prev,
                                  [conflict.code_name]: option.value,
                                }))
                              }
                            >
                              {option.value}
                              {option.record_count > 1 && (
                                <span className="ml-1.5 text-[11px] opacity-70">
                                  {option.record_count}
                                </span>
                              )}
                            </Button>
                          ))}
                          {/* Picking one of the existing values is the common
                              case, but neither may be right: a merge is often
                              when someone notices the codes are all wrong.
                              Typing here overrides the chips - the value is
                              free text on the way to the record either way. */}
                          {/* Empty unless the operator types, so the
                              placeholder is visible and the field explains
                              itself. Mirroring the chosen chip made it look
                              like an unexplained third option. */}
                          <span className="text-[12px] text-[var(--ink-3)]">
                            {t('partnerships.mergeConflictOr', 'or')}
                          </span>
                          <Input
                            aria-label={t(
                              'partnerships.mergeConflictOther',
                              'Or type a different {{field}}',
                              { field: conflict.display_name.toLowerCase() },
                            )}
                            data-testid={`merge-conflict-input-${conflict.code_name}`}
                            value={isCustom ? chosen : ''}
                            onChange={(e) =>
                              setFieldChoices((prev) => ({
                                ...prev,
                                [conflict.code_name]: e.target.value,
                              }))
                            }
                            placeholder={t(
                              'partnerships.mergeConflictOther',
                              'Or type a different {{field}}',
                              { field: conflict.display_name.toLowerCase() },
                            )}
                            className="h-7 w-[220px] text-[12px]"
                          />
                        </div>
                      </div>
                    )
                  })}
                </div>
              </div>
            )}

            <div className="flex flex-wrap items-center justify-between gap-2">
              {/* The other outcome of reviewing duplicates: they are not the
                  same business. Without a way to say so, the only exit is to
                  leave, and the queue proposes the same group again forever. */}
              {duplicateGroup?.key ? (
                <Button
                  variant="outline"
                  data-testid="merge-not-duplicates"
                  disabled={dismissGroup.isPending}
                  onClick={async () => {
                    try {
                      await dismissGroup.mutateAsync(duplicateGroup.key)
                      toast.success(
                        t(
                          'partnerships.mergeDismissed',
                          'Marked as separate businesses. They will not be suggested again.',
                        ),
                      )
                      // Back to the queue they came from, not to a partner
                      // page they never asked for - the point of dismissing is
                      // to carry on working through the list.
                      navigate('/network/partners?tab=duplicates')
                    } catch (err) {
                      toastApiError(
                        err,
                        t(
                          'partnerships.mergeDismissFailed',
                          'Could not save that. Nothing has changed.',
                        ),
                      )
                    }
                  }}
                >
                  {dismissGroup.isPending && (
                    <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                  )}
                  {t('partnerships.mergeNotDuplicates', 'Not the same business')}
                </Button>
              ) : (
                <span />
              )}
              {/* Enabled with nothing ticked - the click says what is missing. */}
              <Button
                onClick={goToReview}
                data-testid="merge-continue"
                disabled={createPlan.isPending}
              >
                {createPlan.isPending && (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                )}
                {t('partnerships.mergeContinue', 'Review this merge')}
              </Button>
            </div>
          </section>
        )}

        {step === 'review' && plan && (
          <section className="space-y-5" data-testid="merge-step-review">
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5">
              <p className="clara-label mb-3">
                {t('partnerships.mergeWhatHappens', 'What happens')}
              </p>
              <ul className="space-y-1.5 text-[13px] text-[var(--ink-2)]">
                <li data-testid="merge-review-registrations">
                  {t(
                    'partnerships.mergeReviewRegistrations',
                    '{{registrations}} move to {{survivor}}.',
                    {
                      registrations: countRegistrations(
                        plan.registrations_affected,
                      ),
                      survivor: plan.survivor_name,
                    },
                  )}
                </li>
                <li>
                  {t('partnerships.mergeReviewPeople', '{{people}} move to {{survivor}}.', {
                    people: countPeople(plan.people_affected),
                    survivor: plan.survivor_name,
                  })}
                </li>
                <li>
                  {/* Selected explicitly rather than via i18next pluralisation:
                      this is the confirm step for an irreversible action, and
                      "1 records are retired" reads as a bug exactly where an
                      operator is deciding. */}
                  {plan.items.length === 1
                    ? t(
                        'partnerships.mergeReviewRetiredOne',
                        '1 record is retired. Nothing is deleted.',
                      )
                    : t(
                        'partnerships.mergeReviewRetired',
                        '{{count}} records are retired. Nothing is deleted.',
                        { count: plan.items.length },
                      )}
                </li>
              </ul>

              {!!impact?.locations_moved?.length && (
                <p data-testid="merge-review-locations">
                  {(() => {
                    const all = impact.locations_moved
                    // Duplicate NAMES are expected here - near-identical records
                    // are exactly what a merge exists to collapse - but listing
                    // one twice reads as a rendering bug. Sample unique names,
                    // keep the true count.
                    const unique = Array.from(new Set(all))
                    const shown = unique.slice(0, 4)
                    const hidden = all.length - shown.length
                    return t(
                      hidden > 0
                        ? 'partnerships.mergeReviewLocationsMore'
                        : 'partnerships.mergeReviewLocations',
                      hidden > 0
                        ? '{{count}} locations move with them, including {{names}} and {{hidden}} more.'
                        : '{{count}} locations move with them: {{names}}.',
                      {
                        count: all.length,
                        names: shown.join(', '),
                        hidden,
                      },
                    )
                  })()}
                </p>
              )}

              <Separator className="my-4" />
              <p className="clara-label mb-2">
                {t('partnerships.mergeRecordsHeading', 'Records being merged in')}
              </p>
              <ul className="space-y-1 text-[13px] text-[var(--ink-2)]">
                {plan.items.map((item) => (
                  <li key={item.uuid} className="flex justify-between">
                    <span>{item.partner_name}</span>
                    <span className="text-[12px] text-[var(--ink-3)]">
                      {t(
                        'partnerships.mergeRowRegistrations',
                        '{{registrations}} registrations',
                        {
                          registrations:
                            item.registrations_affected.toLocaleString(),
                        },
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            {plan.blockers.length > 0 && (
              <div
                className="rounded-[8px] border-[0.5px] border-[var(--status-error)] p-4"
                data-testid="merge-blockers"
              >
                <p className="mb-2 flex items-center gap-1.5 text-[13px] text-[var(--status-error)]">
                  <AlertTriangle className="h-3.5 w-3.5" />
                  {t(
                    'partnerships.mergeBlockedHeading',
                    'This merge cannot run yet',
                  )}
                </p>
                <ul className="space-y-1 text-[12px] text-[var(--ink-2)]">
                  {plan.blockers.map((b, i) => (
                    <li key={`${b.reason}-${i}`}>{b.detail}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="flex justify-between">
              <Button variant="ghost" onClick={() => setStep('choose')}>
                {t('partnerships.mergeBackToChoose', 'Change the selection')}
              </Button>
              <Button
                onClick={confirm}
                data-testid="merge-confirm"
                // Disabled only for a genuine block or in-flight state, never
                // for an incomplete form.
                disabled={startPlan.isPending || !plan.can_proceed}
              >
                {startPlan.isPending && (
                  <Loader2 className="mr-1.5 h-3 w-3 animate-spin" />
                )}
                {plan.items.length === 1
                  ? t('partnerships.mergeConfirmOne', 'Merge 1 record in')
                  : t('partnerships.mergeConfirm', 'Merge {{count}} records in', {
                      count: plan.items.length,
                    })}
              </Button>
            </div>
          </section>
        )}

        {step === 'running' && current && (
          <section className="space-y-4" data-testid="merge-step-running">
            <div className="rounded-[8px] border-[0.5px] border-[var(--rule)] p-5">
              <p className="mb-3 flex items-center gap-2 text-[13px] text-[var(--ink)]">
                {isDone ? (
                  <Check className="h-4 w-4 text-[var(--green)]" />
                ) : (
                  <Loader2 className="h-4 w-4 animate-spin text-[var(--ink-3)]" />
                )}
                {isDone
                  ? t('partnerships.mergeFinished', 'Merge finished.')
                  : t(
                      'partnerships.mergeWorking',
                      'Merging. You can keep working - this continues in the background.',
                    )}
              </p>
              <ul className="space-y-1.5 text-[13px]" data-testid="merge-progress">
                {current.items.map((item) => (
                  <li key={item.uuid} className="flex items-center justify-between">
                    <span className="text-[var(--ink-2)]">{item.partner_name}</span>
                    <span className="text-[12px] text-[var(--ink-3)]">
                      {item.status === 'failed' ? (
                        <span className="text-[var(--status-error)]">
                          {item.error ||
                            t('partnerships.mergeItemFailed', 'Could not be merged')}
                        </span>
                      ) : item.status === 'completed' ? (
                        t('partnerships.mergeItemDone', 'Merged')
                      ) : (
                        t('partnerships.mergeItemWaiting', 'Waiting')
                      )}
                    </span>
                  </li>
                ))}
              </ul>
              {current.status === 'partial' && (
                <p
                  className="mt-3 text-[12px] text-[var(--status-warn)]"
                  data-testid="merge-partial"
                >
                  {t(
                    'partnerships.mergePartial',
                    'Some records were merged and some were not. The ones that failed are listed above and can be retried.',
                  )}
                </p>
              )}
            </div>

            {isDone && (
              <div className="flex justify-end">
                <Button onClick={() => navigate(`/network/${survivorUuid}`)}>
                  {t('partnerships.mergeDone', 'Back to partner')}
                </Button>
              </div>
            )}
          </section>
        )}
      </main>
    </AppShell>
  )
}
